// Перенос остатков долгов листом из Excel: вставить, увидеть, что понято, записать.
//
// ## Откуда взялось
//
// 24.09.2026 заказчик третий раз открыл долги человека, у которого они точно есть, и прочёл:
// «долги до начала работы в программе сюда ещё не перенесены». Надпись была честной, но
// способа перенести не было никакого, кроме заведения каждого долга руками, — двести с лишним
// заходов в карточки. Замысел 15.09.2026 («остатки собираются листом, проверяются заказчиком и
// заливаются пачкой») так и остался без двери.
//
// ## Сначала показать, потом записать
//
// Долг удерживается из зарплаты сам. Поэтому ничего не пишется, пока человек не увидел, что
// программа поняла: кого нашла, кого нет, у кого однофамильцы, сколько по каждому виду и что
// из этого будет удерживаться само. Показ и запись идут одним и тем же запросом сервера — он
// отвечает ровно тем, что запишет.
//
// ## Ошибиться можно, и это исправимо
//
// Повторная вставка того же листа не удваивает долг: второй остаток того же вида человеку не
// переносится. Вставили не тот лист — перенос отменяется целиком, пока по нему ничего не
// удержано.

import { useMemo, useState } from 'react';

import { RiAlertLine, RiCheckboxCircleLine } from '@remixicon/react';

import type {
  OpeningBalanceRowOut,
  OpeningBalancesCancelOut,
  OpeningBalancesOut,
  OpeningBalancesPlanOut,
} from '@/shared/api/endpoints/losses';
import type { ApiError } from '@/shared/errors/api-error';
import { useAuthStore } from '@/shared/auth/store';
import { useПереносДолгов } from '@/shared/debts/перенос';
import { useDebtTypes } from '@/shared/dictionaries/debt-types';
import { formatRubles } from '@/shared/money/format';
import { useLegalEntitiesQuery } from '@/shared/org/queries';
import { can } from '@/shared/rbac/permissions';
import { countLabel } from '@/shared/text/plural';
import { formatDateOnly } from '@/shared/time/format';
import * as Button from '@/shared/ui/button';
import { ErrorAlert } from '@/shared/ui/error-alert';
import * as Hint from '@/shared/ui/hint';
import * as LinkButton from '@/shared/ui/link-button';
import { NO_VALUE, SelectField } from '@/shared/ui/select-field';
import { TextArea } from '@/shared/ui/text-area';
import { TextField } from '@/shared/ui/text-field';

import { useCancelOpeningBalances, usePlanOpeningBalances } from '../../queries';

import type { РазборЛиста } from './лист-остатков';
import { разобратьЛист } from './лист-остатков';

const EDIT_PERMISSION = 'core.debt.edit';

const ДОЛГИ = ['долг', 'долга', 'долгов'] as const;

/** Состояния строки, которые не переносятся, — словами для заголовка группы. */
const ГРУППЫ: ReadonlyArray<{ state: OpeningBalanceRowOut['state']; заголовок: string }> = [
  { state: 'ambiguous', заголовок: 'Однофамильцы: выберите, чей долг' },
  { state: 'not_found', заголовок: 'Не нашли в программе' },
  { state: 'repeated', заголовок: 'Повтор в листе' },
  { state: 'exists', заголовок: 'Уже перенесено раньше' },
];

export function OpeningBalancesPanel() {
  const права = useAuthStore((state) => state.permissions);
  const можноПравить = can(права, EDIT_PERMISSION);
  const юрлица = useLegalEntitiesQuery();
  const виды = useDebtTypes();
  const перенос = useПереносДолгов();
  const показ = usePlanOpeningBalances();
  const запись = usePlanOpeningBalances();

  const [юрлицо, setЮрлицо] = useState<string>(NO_VALUE);
  const [вставка, setВставка] = useState('');
  const [видБезСлова, setВидБезСлова] = useState<string>(NO_VALUE);
  const [основание, setОснование] = useState('');
  const [разбор, setРазбор] = useState<РазборЛиста | null>(null);
  // Выбор среди однофамильцев: номер строки листа → человек.
  const [выбор, setВыбор] = useState<Record<number, string>>({});
  const [план, setПлан] = useState<OpeningBalancesPlanOut | null>(null);
  const [итог, setИтог] = useState<OpeningBalancesPlanOut | null>(null);
  const [запрет, setЗапрет] = useState<string | null>(null);

  const справочникВидов = useMemo(
    () => (виды.data?.items ?? []).map((вид) => ({ code: вид.code, title: вид.title })),
    [виды.data],
  );

  function строкиДляСервера(текущий: РазборЛиста, текущийВыбор: Record<number, string>) {
    return текущий.строки.map((строка, номер) => ({
      line: строка.текст,
      name: строка.имя,
      debt_type: строка.вид,
      amount: строка.сумма,
      employee_id: текущийВыбор[номер] ?? null,
    }));
  }

  function показать(текущий: РазборЛиста, текущийВыбор: Record<number, string>) {
    if (юрлицо === NO_VALUE) {
      setЗапрет('Выберите юрлицо: долг удерживается в ведомости этого юрлица.');
      return;
    }
    if (текущий.строки.length === 0) {
      setПлан(null);
      setЗапрет(
        текущий.неРазобрано.length > 0
          ? 'Ни одна строка не разобрана — причины у каждой ниже. Поправьте лист и разберите снова.'
          : 'Вставьте лист остатков: из Excel скопируйте строки с фамилией, видом долга и суммой.',
      );
      return;
    }
    setЗапрет(null);
    показ.mutate(
      {
        legal_entity_id: юрлицо,
        basis: основание.trim(),
        dry_run: true,
        rows: строкиДляСервера(текущий, текущийВыбор),
      },
      { onSuccess: setПлан },
    );
  }

  function разобрать() {
    setИтог(null);
    if (виды.data === undefined) {
      setЗапрет('Справочник видов долга ещё не приехал: подождите секунду и нажмите снова.');
      return;
    }
    const новый = разобратьЛист(
      вставка,
      справочникВидов,
      видБезСлова === NO_VALUE ? null : видБезСлова,
    );
    setРазбор(новый);
    setВыбор({});
    показать(новый, {});
  }

  function записать() {
    if (!можноПравить) {
      setЗапрет('Переносит финансовый отдел: у вашей роли нет права заводить долги.');
      return;
    }
    if (разбор === null || план === null) {
      setЗапрет('Сначала разберите лист: без этого не видно, что будет записано.');
      return;
    }
    if (план.ready === 0) {
      setЗапрет('Переносить нечего: ни одна строка не готова. Причины — в списке выше.');
      return;
    }
    if (основание.trim().length < 4) {
      setЗапрет(
        'Напишите основание: откуда эти остатки. Оно ляжет в каждый долг, и по нему потом объясняют человеку, откуда долг.',
      );
      return;
    }
    setЗапрет(null);
    запись.mutate(
      {
        legal_entity_id: юрлицо,
        basis: основание.trim(),
        dry_run: false,
        rows: строкиДляСервера(разбор, выбор),
      },
      {
        onSuccess: (ответ) => {
          setИтог(ответ);
          setПлан(null);
          setРазбор(null);
          setВыбор({});
          setВставка('');
        },
      },
    );
  }

  return (
    // Поля панели кладёт сама стопка панелей (`PanelStack`): свой отступ здесь дал бы двойное.
    <div className='flex flex-col gap-4'>
      <p className='max-w-prose text-paragraph-sm text-text-sub-600'>
        Долги, которые были до начала работы в программе, переносятся одним листом. Скопируйте из
        Excel строки «фамилия — за что — сумма» либо таблицу, где у каждого вида долга свой столбец,
        как в вашей ведомости: первой строкой — заголовки «ФИО, Билеты, Обучение, Форма». Программа
        сначала покажет, что поняла, и ничего не запишет, пока вы не нажмёте «Перенести».
      </p>

      <ПереносУжеБыл перенос={перенос.data} />

      <div className='flex flex-col gap-3'>
        <SelectField
          id='transfer-entity'
          label='Юрлицо'
          required
          value={юрлицо}
          onChange={(значение) => {
            setЮрлицо(значение);
            setПлан(null);
          }}
          options={(юрлица.data?.items ?? []).map((строка) => ({
            value: строка.id,
            label: строка.short_name ?? строка.name,
          }))}
          pending={юрлица.isPending}
          failed={юрлица.isError}
          failedError={юрлица.error as ApiError | null}
          placeholder='выберите юрлицо'
          hint='Долг удерживается в ведомостях этого юрлица.'
        />
        <TextArea
          id='transfer-paste'
          label='Лист остатков из Excel'
          value={вставка}
          onChange={(значение) => {
            setВставка(значение);
            setРазбор(null);
            setПлан(null);
            setИтог(null);
          }}
          rows={7}
          placeholder={'ФИО\tБилеты\tОбучение\tФорма\nКарастелев Д.\t5 000\t\t3 600'}
        />
        <SelectField
          id='transfer-default-type'
          label='Вид долга для строк, где он не указан'
          value={видБезСлова}
          onChange={setВидБезСлова}
          options={справочникВидов.map((вид) => ({ value: вид.code, label: вид.title }))}
          noneLabel='не подставлять — такие строки назвать'
          placeholder='не подставлять'
          pending={виды.isPending}
          failed={виды.isError}
          failedError={виды.error}
        />
        <div>
          <Button.Root
            type='button'
            variant='neutral'
            mode='stroke'
            size='small'
            onClick={разобрать}
          >
            {показ.isPending ? 'Разбираем…' : 'Разобрать лист'}
          </Button.Root>
        </div>
      </div>

      {показ.error !== null && <ErrorAlert error={показ.error} />}

      {разбор !== null && разбор.неРазобрано.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='text-label-sm text-text-strong-950'>
            Не разобрано: {countLabel(разбор.неРазобрано.length, ['строка', 'строки', 'строк'])}
          </span>
          {разбор.неРазобрано.map((строка) => (
            <span
              key={`${строка.номер}-${строка.причина}`}
              className='text-paragraph-xs text-text-sub-600'
            >
              Строка {строка.номер}: {строка.текст.replace(/\t/g, ' · ')} — {строка.причина}
            </span>
          ))}
        </div>
      )}

      {план !== null && разбор !== null && (
        <Отчёт
          план={план}
          выбор={выбор}
          onВыбор={(номер, человек) => {
            const новый = { ...выбор, [номер]: человек };
            setВыбор(новый);
            показать(разбор, новый);
          }}
        />
      )}

      {план !== null && (
        <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
          <TextField
            id='transfer-basis'
            label='Основание'
            value={основание}
            onChange={setОснование}
            placeholder='например: лист остатков из ведомости за август'
            hint='Ляжет в каждый перенесённый долг.'
          />
          <div>
            <Button.Root
              type='button'
              variant='primary'
              mode='filled'
              size='small'
              onClick={записать}
            >
              {запись.isPending
                ? 'Переносим…'
                : план.ready > 0
                  ? `Перенести ${countLabel(план.ready, ДОЛГИ)} на ${formatRubles(план.ready_total)}`
                  : 'Перенести'}
            </Button.Root>
          </div>
        </div>
      )}

      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {запись.error !== null && <ErrorAlert error={запись.error} />}
      {итог !== null && <ИтогПереноса итог={итог} />}

      {перенос.data?.migrated === true && <ОтменаПереноса можноПравить={можноПравить} />}
    </div>
  );
}

function ПереносУжеБыл({ перенос }: { перенос: OpeningBalancesOut | undefined }) {
  if (перенос?.migrated !== true) {
    return null;
  }
  const сколько =
    перенос.debts !== null && перенос.debts !== undefined
      ? `${countLabel(перенос.debts, ДОЛГИ)}${
          перенос.total !== null && перенос.total !== undefined
            ? ` на ${formatRubles(перенос.total)}`
            : ''
        }`
      : null;
  return (
    <p className='rounded-lg bg-bg-weak-50 p-3 text-paragraph-sm text-text-sub-600'>
      Перенос уже был{сколько === null ? '' : `: ${сколько}`}
      {перенос.migrated_at ? `, последний — ${formatDateOnly(перенос.migrated_at)}` : ''}. Можно
      дописать остальных: остаток, который человеку уже перенесли, второй раз не перенесётся.
    </p>
  );
}

function Отчёт({
  план,
  выбор,
  onВыбор,
}: {
  план: OpeningBalancesPlanOut;
  выбор: Record<number, string>;
  onВыбор: (номер: number, человек: string) => void;
}) {
  const готовые = план.rows.filter((строка) => строка.state === 'ready');
  const [всех, setВсех] = useState(false);
  return (
    <div className='flex flex-col gap-3 border-t border-stroke-soft-200 pt-4'>
      <span className='text-label-md text-text-strong-950'>{план.summary}</span>
      <span className='text-paragraph-xs text-text-sub-600'>
        Пока ничего не записано: это то, что будет перенесено после нажатия «Перенести».
      </span>

      {план.by_type.length > 0 && (
        <ul className='flex flex-col gap-1'>
          {план.by_type.map((вид) => (
            <li key={вид.debt_type} className='flex flex-col text-paragraph-sm'>
              <span className='text-text-strong-950'>
                {вид.title} — {countLabel(вид.count, ДОЛГИ)} на {formatRubles(вид.total)}
              </span>
              {!вид.withheld_by_default && (
                <span className='text-paragraph-xs text-text-sub-600'>
                  Сам удерживаться не будет: у этого вида нет суммы в месяц по умолчанию. После
                  переноса задайте график в карточке долга.
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {ГРУППЫ.map(({ state, заголовок }) => {
        const строки = план.rows.filter((строка) => строка.state === state);
        if (строки.length === 0) {
          return null;
        }
        return (
          <div key={state} className='flex flex-col gap-2'>
            <span className='flex items-center gap-1.5 text-label-sm text-text-strong-950'>
              <RiAlertLine className='size-4 shrink-0 text-warning-base' aria-hidden />
              {заголовок}: {строки.length}
            </span>
            {строки.map((строка) => (
              <div key={строка.index} className='flex flex-col gap-1 rounded-lg bg-bg-weak-50 p-3'>
                <span className='text-paragraph-sm text-text-strong-950'>{строка.line}</span>
                {строка.reason && (
                  <span className='text-paragraph-xs text-text-sub-600'>{строка.reason}</span>
                )}
                {state === 'ambiguous' && (
                  <SelectField
                    id={`transfer-pick-${строка.index}`}
                    label='Чей долг'
                    value={выбор[строка.index] ?? NO_VALUE}
                    onChange={(значение) => {
                      if (значение !== NO_VALUE) {
                        onВыбор(строка.index, значение);
                      }
                    }}
                    options={строка.candidates.map((кандидат) => ({
                      value: кандидат.employee_id,
                      label: кандидат.label,
                    }))}
                    placeholder='выберите человека'
                  />
                )}
              </div>
            ))}
          </div>
        );
      })}

      {готовые.length > 0 && (
        <div className='flex flex-col gap-2'>
          <div>
            <LinkButton.Root
              type='button'
              variant='primary'
              size='small'
              underline
              onClick={() => setВсех((было) => !было)}
            >
              {всех ? 'Скрыть список' : `Кто перенесётся: ${countLabel(готовые.length, ДОЛГИ)}`}
            </LinkButton.Root>
          </div>
          {всех && (
            <ul className='flex flex-col gap-0.5'>
              {готовые.map((строка) => (
                <li
                  key={строка.index}
                  className='flex items-baseline justify-between gap-3 text-paragraph-xs text-text-sub-600'
                >
                  <span className='truncate'>
                    {строка.employee_name} · {строка.debt_type_title}
                  </span>
                  <span className='shrink-0 tabular-nums text-text-strong-950'>
                    {formatRubles(строка.amount)}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function ИтогПереноса({ итог }: { итог: OpeningBalancesPlanOut }) {
  const непереносённые = итог.rows.filter((строка) => строка.state !== 'written');
  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
      <span className='flex items-center gap-1.5 text-label-sm text-text-strong-950'>
        <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' aria-hidden />
        {итог.summary}
      </span>
      {итог.ready > 0 && (
        <span className='text-paragraph-xs text-text-sub-600'>
          Долги уже видны в карточках людей и в реестре. Удерживаться они начнут с ближайшего
          расчёта ведомости.
        </span>
      )}
      {непереносённые.map((строка) => (
        <span
          key={строка.index}
          className='flex items-start gap-1.5 text-paragraph-xs text-text-sub-600'
        >
          <RiAlertLine className='mt-0.5 size-4 shrink-0 text-error-base' aria-hidden />
          <span>
            <span className='text-text-strong-950'>{строка.line}</span> — {строка.reason}
          </span>
        </span>
      ))}
    </div>
  );
}

function ОтменаПереноса({ можноПравить }: { можноПравить: boolean }) {
  const отмена = useCancelOpeningBalances();
  const [открыто, setОткрыто] = useState(false);
  const [основание, setОснование] = useState('');
  const [показано, setПоказано] = useState<OpeningBalancesCancelOut | null>(null);
  const [итог, setИтог] = useState<OpeningBalancesCancelOut | null>(null);
  const [запрет, setЗапрет] = useState<string | null>(null);

  if (!открыто) {
    return (
      <div className='border-t border-stroke-soft-200 pt-4'>
        <Button.Root
          type='button'
          variant='neutral'
          mode='ghost'
          size='xsmall'
          onClick={() => setОткрыто(true)}
        >
          Вставили не тот лист? Отменить перенос
        </Button.Root>
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-2 border-t border-stroke-soft-200 pt-4'>
      <span className='text-label-sm text-text-strong-950'>Отмена переноса</span>
      <span className='max-w-prose text-paragraph-xs text-text-sub-600'>
        Отменяются все перенесённые долги, по которым ещё ничего не удержано: они станут
        аннулированными, и лист можно будет перенести заново. Долги, по которым уже удерживали,
        остаются — эти деньги ушли из зарплаты.
      </span>
      <TextField
        id='transfer-cancel-basis'
        label='Почему отменяете'
        value={основание}
        onChange={setОснование}
        placeholder='например: вставили лист не за тот месяц'
      />
      <div className='flex flex-wrap gap-2'>
        <Button.Root
          type='button'
          variant='neutral'
          mode='stroke'
          size='xsmall'
          onClick={() => {
            setЗапрет(null);
            отмена.mutate({ basis: основание.trim(), dry_run: true }, { onSuccess: setПоказано });
          }}
        >
          {отмена.isPending && показано === null ? 'Считаем…' : 'Показать, что отменится'}
        </Button.Root>
        {показано !== null && показано.cancelled > 0 && итог === null && (
          <Button.Root
            type='button'
            variant='error'
            mode='filled'
            size='xsmall'
            onClick={() => {
              if (!можноПравить) {
                setЗапрет('Отменяет финансовый отдел: у вашей роли нет права менять долги.');
                return;
              }
              if (основание.trim().length < 4) {
                setЗапрет('Напишите, почему отменяете: это ляжет в историю каждого долга.');
                return;
              }
              setЗапрет(null);
              отмена.mutate({ basis: основание.trim(), dry_run: false }, { onSuccess: setИтог });
            }}
          >
            Отменить {countLabel(показано.cancelled, ДОЛГИ)} на{' '}
            {formatRubles(показано.cancelled_total)}
          </Button.Root>
        )}
      </div>
      {показано !== null && итог === null && (
        <span className='text-paragraph-xs text-text-sub-600'>{показано.summary}</span>
      )}
      {(итог ?? показано)?.kept.map((долг) => (
        <span
          key={`${долг.employee_name}-${долг.debt_type_title}`}
          className='text-paragraph-xs text-text-sub-600'
        >
          Остаётся: {долг.employee_name} · {долг.debt_type_title} — {долг.reason}
        </span>
      ))}
      {запрет !== null && <Hint.Root hasError>{запрет}</Hint.Root>}
      {отмена.error !== null && <ErrorAlert error={отмена.error} />}
      {итог !== null && (
        <span className='flex items-center gap-1.5 text-label-sm text-text-strong-950'>
          <RiCheckboxCircleLine className='size-4 shrink-0 text-success-base' aria-hidden />
          {итог.summary}
        </span>
      )}
    </div>
  );
}
